import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

@Injectable({
    providedIn: 'root',
})
export class BaseService {
    private baseUrl = environment.apiUrl;

    private ERROR_MESSAGE = 'An unexpected error occurred.';

    constructor(private http: HttpClient, private authService: AuthService) {
    }

    private createHeaders(contentType: string = 'application/json'): HttpHeaders {
        const token = this.authService.getToken();
        const headersConfig: { [key: string]: string } = {
            'Content-Type': contentType,
        };

        if (token) {
            headersConfig['Authorization'] = `Bearer ${ token }`;
        }

        return new HttpHeaders(headersConfig);
    }

    /**
     * Creates http headers specific to form data skipping header Content-Type as it is automatically added.
     */
    private createFormDataHeaders(): HttpHeaders {
        const token = this.authService.getToken();
        const headersConfig: { [key: string]: string } = {};

        if (token) {
            headersConfig['Authorization'] = `Bearer ${ token }`;
        }

        return new HttpHeaders(headersConfig);
    }

    /**
     * Handle the URL for the request.
     *
     * @param endpoint The endpoint to access.
     * @return The URL for the request.
     */
    private handleUrl(endpoint: string): string {
        return `${ this.baseUrl }/${ endpoint }`;
    }

    /**
     * Handle an error from the request.
     *
     * @param error The error to handle.
     * @return An error message.
     */
    private handleError(error: any): never {
        console.error('An error occurred:', error);

        let errorMessage = this.ERROR_MESSAGE;

        if (error instanceof HttpErrorResponse) {
            if (error.status === 401 || error.status === 403) {
                // If the error is due to an invalid or expired token, clear the token and redirect to login
                console.error('Unauthorized access: Clearing token and redirecting to login.');
                this.authService.logout().then(() => {
                    // Redirect to log in
                    window.location.href = '/login';
                });
                errorMessage = 'Session expired. Please log in again.';
            } else if (error.error) {
                const serverError = error.error;

                if (serverError.message) {
                    errorMessage = serverError.message;
                }

                if (serverError.details && Array.isArray(serverError.details)) {
                    if (errorMessage === this.ERROR_MESSAGE) {
                        errorMessage = 'An error occurred.';
                    }
                    errorMessage += ' Details: ' + serverError.details.join(', ');
                }
            } else {
                errorMessage = `Server returned code ${ error.status }: ${ error.statusText }`;
            }
        } else if (error.message) {
            errorMessage = error.message;
        }

        throw new Error(errorMessage);
    }

    /**
     * Perform a GET request.
     *
     * @param endpoint The endpoint to access.
     * @param params The parameters for the request.
     * @return The response from the request.
     */
    public async get<T>(endpoint: string, params?: HttpParams): Promise<T> {
        const url = this.handleUrl(endpoint);
        try {
            return await lastValueFrom(
                this.http.get<T>(url, { headers: this.createHeaders(), params })
            );
        } catch (error) {
            this.handleError(error);
        }
    }

    /**
     * Perform a POST request.
     *
     * @param endpoint The endpoint to access.
     * @param body The body of the request.
     * @param params The parameters for the request.
     * @return The response from the request.
     */
    public async post<T>(endpoint: string, body: any, params?: HttpParams): Promise<T> {
        const url = this.handleUrl(endpoint);
        let requestHeaders: HttpHeaders;
        if (body instanceof FormData) {
            requestHeaders = this.createFormDataHeaders();
        } else {
            requestHeaders = this.createHeaders();
        }
        try {
            return await lastValueFrom(
                this.http.post<T>(url, body, { headers: requestHeaders, params })
            );
        } catch (error) {
            this.handleError(error);
        }
    }

    /**
     * Perform a PUT request.
     *
     * @param endpoint The endpoint to access.
     * @param body The body of the request.
     * @param params The parameters for the request.
     * @return The response from the request.
     */
    public async put<T>(endpoint: string, body: any, params?: HttpParams): Promise<T> {
        const url = this.handleUrl(endpoint);
        let requestHeaders: HttpHeaders;
        if (body instanceof FormData) {
            requestHeaders = this.createFormDataHeaders();
        } else {
            requestHeaders = this.createHeaders();
        }
        try {
            return await lastValueFrom(
                this.http.put<T>(url, body, { headers: requestHeaders, params })
            );
        } catch (error) {
            this.handleError(error);
        }
    }

    /**
     * Perform a DELETE request.
     *
     * @param endpoint The endpoint to access.
     * @param params The parameters for the request.
     * @return The response from the request.
     */
    public async delete<T>(endpoint: string, params?: HttpParams): Promise<T> {
        const url = this.handleUrl(endpoint);
        try {
            return await lastValueFrom(
                this.http.delete<T>(url, { headers: this.createHeaders(), params })
            );
        } catch (error) {
            this.handleError(error);
        }
    }

    /**
     * Perform a PATCH request.
     *
     * @param endpoint The endpoint to access.
     * @param body The body of the request.
     * @param params The parameters for the request.
     * @return The response from the request.
     */
    public async patch<T>(endpoint: string, body: any, params?: HttpParams): Promise<T> {
        const url = this.handleUrl(endpoint);
        try {
            return await lastValueFrom(
                this.http.patch<T>(url, body, { headers: this.createHeaders(), params })
            );
        } catch (error) {
            this.handleError(error);
        }
    }

    /**
     * Perform a Blob request.
     *
     * @param endpoint The endpoint to access.
     * @param params The parameters for the request.
     * @return The response from the request.
     */
    public async getBlob(endpoint: string, params?: HttpParams): Promise<Blob> {
        const url = this.handleUrl(endpoint);
        try {
            return await lastValueFrom(
                this.http.get(url, { headers: this.createHeaders(), params, responseType: 'blob' })
            );
        } catch (error) {
            this.handleError(error);
        }
    }

    async uploadFile<T>(endpoint: string, formData: FormData, params?: HttpParams): Promise<T> {
        const url = this.handleUrl(endpoint);
        try {
            return await lastValueFrom(
                this.http.put<T>(
                    url, formData,
                    { headers: this.createHeaders('multipart/form-data').delete('Content-Type'), params }
                )
            );
        } catch (error) {
            this.handleError(error);
        }
    }

}